import { useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";

import { isAccessTokenExpired, AuthState } from "../reducers/authReducer";
import {
  selectLoading,
  selectRequestStatuses,
  selectIsLoading,
  selectHasLoaded,
  selectLoadedAt,
  selectHasBeenRequested,
  RequestStatusesState,
  selectIsLoadingAny,
  selectHasBeenRequestedAny,
  selectHasLoadedAny,
  selectRequestError,
} from "../reducers/requestStatusesReducer";
import { AsyncSagaActionNoArgs, AsyncSagaAction } from "../utils/asyncActions";

type State = {
  requestStatuses: RequestStatusesState;
};

type RequestAction = {
  getRequestKey: (args: any) => string;
};

type RequestKeyArg = string | RequestAction;

type RequestKeyRegExpArg = RegExp | RequestAction;

type AnyAsyncSagaAction = AsyncSagaAction<any> | AsyncSagaActionNoArgs;

// When an action has been created with the createAsyncSaga() helper it can be passed directly
// to these hooks instead of a requestKey string. Any other arguments provided to the hook
// will be passed through to the action's getKey() method.
//
// Example using createAsyncSaga() Action without arguments:
//     const isLoadingExample = useIsLoading(fetchExample);
//
// Example using createAsyncSaga() Action with arguments:
//     const isLoadingDermpathUser1 = useIsLoading(fetchUser, "dermpath", 1);
//
// Example using requestKey string (equal to previous example):
//     const isLoadingDermpathUser1 = useIsLoading("users/dermpath/1");
//

const getRequestKey = (requestKeyArg: RequestKeyArg, args?: any) => {
  const requestAction = requestKeyArg as RequestAction;
  if (requestAction?.getRequestKey)
    return requestAction.getRequestKey.call(null, args);
  return requestKeyArg as string;
};

const getRequestKeyRegExp = (requestKeyRegExpArg: RequestKeyRegExpArg) => {
  const requestAction = requestKeyRegExpArg as RequestAction;
  if (requestAction?.getRequestKey)
    return new RegExp(
      requestAction.getRequestKey.call(null, {}).replace("undefined", "\\d+")
    );
  return requestKeyRegExpArg as RegExp;
};

export const useRequestStatuses = () => useSelector(selectRequestStatuses);

export const useLoading = (requestKeyArg: RequestKeyArg, args?: any) =>
  useSelector((state: State) =>
    selectLoading(state, getRequestKey(requestKeyArg, args))
  );

export const useIsLoading = (requestKeyArg: RequestKeyArg, args?: any) =>
  useSelector((state: State) =>
    selectIsLoading(state, getRequestKey(requestKeyArg, args))
  );

export const useIsLoadingAny = (requestKeyRegExpArg: RequestKeyRegExpArg) =>
  useSelector((state: State) =>
    selectIsLoadingAny(state, getRequestKeyRegExp(requestKeyRegExpArg))
  );

export const useHasBeenRequested = (requestKeyArg: RequestKeyArg, args?: any) =>
  useSelector((state: State) =>
    selectHasBeenRequested(state, getRequestKey(requestKeyArg, args))
  );

export const useHasBeenRequestedAny = (
  requestKeyRegExpArg: RequestKeyRegExpArg
) =>
  useSelector((state: State) =>
    selectHasBeenRequestedAny(state, getRequestKeyRegExp(requestKeyRegExpArg))
  );

export const useLoadedAt = (requestKeyArg: RequestKeyArg, args?: any) =>
  useSelector((state: State) =>
    selectLoadedAt(state, getRequestKey(requestKeyArg, args))
  );

export const useHasLoaded = (requestKeyArg: RequestKeyArg, args?: any) =>
  useSelector((state: State) =>
    selectHasLoaded(state, getRequestKey(requestKeyArg, args))
  );

export const useHasLoadedAny = (requestKeyRegExpArg: RequestKeyRegExpArg) =>
  useSelector((state: State) =>
    selectHasLoadedAny(state, getRequestKeyRegExp(requestKeyRegExpArg))
  );

export const useRequestError = (requestKeyArg: RequestKeyArg, args?: any) =>
  useSelector((state: State) =>
    selectRequestError(state, getRequestKey(requestKeyArg, args))
  );

const useRequestBase = (action: AnyAsyncSagaAction, args?: any) => {
  const dispatch = useDispatch() as Function;
  const loggedIn = useSelector(
    ({ auth }: { auth: AuthState }) => !isAccessTokenExpired(auth)
  );
  const loading = useLoading(action, args);
  const loadedAt = useLoadedAt(action, args);

  return { dispatch, loggedIn, loading, loadedAt };
};

//
// useRequestAfterFirstSiteLoad()
//
// Makes a request only once after the first time the site is loaded.
// Returns true if the request has been made successfully at least once.
// TODO: Implement redux-perist (or an alternative)
// Requires redux-persist to be meaningful, otherwise it is functionally equivalent to useRequestAfterEveryLoad().
//

export const useRequestAfterFirstSiteLoad = (
  action: AnyAsyncSagaAction,
  args?: any
) => {
  const { dispatch, loggedIn, loading, loadedAt } = useRequestBase(
    action,
    args
  );

  useEffect(() => {
    if (loggedIn && loading === undefined && !loadedAt) {
      dispatch(action(args));
    }
  }, [loggedIn]);

  return !!loadedAt;
};

//
// useRequestAfterEverySiteLoad()
//
// Makes a request each time the site is loaded or refreshed.
// Returns true if the request has been made successfully at least once.
// If the request was loaded previously then the timestamp of the last request is added as updated_at__gte.
//

export const useRequestAfterEverySiteLoad = (
  action: AnyAsyncSagaAction,
  args: any = {}
) => {
  const { dispatch, loggedIn, loading, loadedAt } = useRequestBase(
    action,
    args
  );

  useEffect(() => {
    // We don't persist the loading state, so if our requestKey is undefined
    // it means we haven't made the request since loading the site.
    if (loggedIn && loading === undefined) {
      if (loadedAt) args.updated_at__gte = loadedAt;
      dispatch(action(args));
    }
  }, [loggedIn]);

  return !!loadedAt;
};

//
// useRequestAfterSiteLoadEveryNMinutes()
//
// Makes a reqeust each time the site is loaded or refreshed, but only if a specified
// amount of time has passed since the last time the request was loaded.
// Returns true if the request has been made successfully at least once.
// If the request was loaded previously then the timestamp of the last request is added as updated_at__gte.
//

export const useRequestAfterSiteLoadEveryNMinutes = (
  action: AnyAsyncSagaAction,
  args: any = {},
  minutes: number
) => {
  const { dispatch, loggedIn, loading, loadedAt } = useRequestBase(
    action,
    args
  );

  useEffect(() => {
    if (
      loggedIn &&
      !loading &&
      (!loadedAt || Date.now() - loadedAt >= minutes * 60 * 1000)
    ) {
      if (loadedAt) args.updated_at__gte = loadedAt;
      dispatch(action(args));
    }
  }, [loggedIn]);

  return !!loadedAt;
};
